import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    model,
    ModelSignal,
    output,
    OutputEmitterRef,
    signal,
    WritableSignal,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { LayerAttributeForm } from '@connect-services/client/pages/filter/layer/create-layer-filter/create-edit-layer-filter.component';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    LayerAttributeCondition,
    MapFilterAttributesDto,
    MapFilterService as SdkMapFilterService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

enum LayerAttributeFormKeys {
    COLUMN = 'column',
    COMPARATOR = 'comparator',
    VALUE = 'value',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingComponent, RessourcesModule, SelectComponent],
    selector: 'app-layer-attribute-filter-form',
    styleUrl: './layer-attribute-filter-form.component.scss',
    templateUrl: './layer-attribute-filter-form.component.html',
})
export class LayerAttributeFilterFormComponent {
    public readonly editDisabled: InputSignal<boolean> = input<boolean>(false);
    public readonly layerAttributeForm: InputSignal<LayerAttributeForm> = input.required<LayerAttributeForm>();
    public readonly layerAttributeFormId: InputSignal<number> = input.required<number>();
    public readonly layerAttributesOptions: InputSignal<SelectOption<string>[]> =
        input.required<SelectOption<string>[]>();
    public readonly layerTableName: InputSignal<string | null> = input.required<string | null>();
    public remove: OutputEmitterRef<void> = output<void>();

    protected attributeComparatorOptions: SelectOption<LayerAttributeCondition.ComparatorEnum>[] = [];
    protected readonly currentValue: WritableSignal<SelectOption<string>[]> = signal<SelectOption<string>[]>([]);
    protected readonly hasTooManyDistinctValues: WritableSignal<boolean> = signal(false);
    protected readonly isLoading: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly LayerAttributeFormKeys = LayerAttributeFormKeys;
    protected readonly layerAttributeValueOptions: WritableSignal<SelectOption<string>[]> = signal([]);
    protected readonly resetValueColumn = signal(0);
    protected readonly selectedColumnOption: ModelSignal<SelectOption<string>[]> = model<SelectOption<string>[]>([]);
    protected readonly selectedComparatorOption: ModelSignal<SelectOption<LayerAttributeCondition.ComparatorEnum>[]> =
        model<SelectOption<LayerAttributeCondition.ComparatorEnum>[]>([]);
    protected readonly selectedValueOption: ModelSignal<SelectOption<string>[]> = model<SelectOption<string>[]>([]);

    private readonly mapFilterService = inject(MapFilterService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sdkMapFilterService = inject(SdkMapFilterService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.attributeComparatorOptions = [
            { label: '=', value: LayerAttributeCondition.ComparatorEnum.EQUALS },
            { label: '!=', value: LayerAttributeCondition.ComparatorEnum.NOT_EQUALS },
            { label: '>', value: LayerAttributeCondition.ComparatorEnum.GREATER_THAN },
            { label: '>=', value: LayerAttributeCondition.ComparatorEnum.GREATER_OR_EQUALS },
            { label: '<', value: LayerAttributeCondition.ComparatorEnum.LESS_THAN },
            { label: '<=', value: LayerAttributeCondition.ComparatorEnum.LESS_OR_EQUALS },
            {
                label: this.translocoService.translate(`mapFilter.${LayerAttributeCondition.ComparatorEnum.CONTAINS}`),
                value: LayerAttributeCondition.ComparatorEnum.CONTAINS,
            },
            {
                label: this.translocoService.translate(
                    `mapFilter.${LayerAttributeCondition.ComparatorEnum.NOT_CONTAINS}`,
                ),
                value: LayerAttributeCondition.ComparatorEnum.NOT_CONTAINS,
            },
            {
                label: this.translocoService.translate(
                    `mapFilter.${LayerAttributeCondition.ComparatorEnum.STARTS_WITH}`,
                ),
                value: LayerAttributeCondition.ComparatorEnum.STARTS_WITH,
            },
        ];

        effect(() => {
            this.selectedColumnOption.set(
                this.layerAttributesOptions().filter(
                    (option: SelectOption<string>) =>
                        option.value === this.layerAttributeForm().get(LayerAttributeFormKeys.COLUMN)!.value,
                ),
            );

            this.selectedComparatorOption.set(
                this.attributeComparatorOptions.filter(
                    (option: SelectOption<LayerAttributeCondition.ComparatorEnum>) =>
                        option.value === this.layerAttributeForm().get(LayerAttributeFormKeys.COMPARATOR)!.value,
                ),
            );
        });

        effect(async () => {
            if (!this.selectedColumnOption().length) {
                return;
            }

            await this.searchAttributeValues();
        });
    }

    protected async setValue(key: LayerAttributeFormKeys, value: string[]): Promise<void> {
        const selectedValue = value?.[0] ?? '';
        this.layerAttributeForm().get(key)?.setValue(selectedValue);

        if (key === LayerAttributeFormKeys.VALUE) {
            const option = this.buildSelectOption(selectedValue);

            this.currentValue.set(option);
            this.selectedValueOption.set(option);
        }

        if (key === LayerAttributeFormKeys.COLUMN) {
            this.resetOperatorAndValueColumns();

            await this.searchAttributeValues();
        }

        this.layerAttributeForm().get(key)?.markAsDirty();
        this.layerAttributeForm().updateValueAndValidity();

        if (!this.mapFilterService.previewResultFilters()) {
            return;
        }
        this.mapFilterService.clearPreview();
    }

    private buildSelectOption(value: string): SelectOption<string>[] {
        if (!value) {
            return [];
        }

        return [
            {
                label: value,
                value: value,
            },
        ];
    }

    private resetOperatorAndValueColumns(): void {
        this.resetValueColumn.update((value) => value + 1);

        this.layerAttributeForm().get(LayerAttributeFormKeys.COMPARATOR)?.setValue(undefined);
        this.layerAttributeForm().get(LayerAttributeFormKeys.VALUE)?.setValue('');

        this.selectedComparatorOption.set([]);
        this.selectedValueOption.set([]);

        this.layerAttributeValueOptions.set([]);
        this.currentValue.set([]);

        this.hasTooManyDistinctValues.set(false);
    }

    private async searchAttributeValues(): Promise<void> {
        this.isLoading.set(true);
        this.hasTooManyDistinctValues.set(false);
        try {
            const currentValue: string = this.layerAttributeForm().get(LayerAttributeFormKeys.VALUE)!.value;
            this.currentValue.set(currentValue ? [{ label: currentValue, value: currentValue }] : []);

            const attributeValues: MapFilterAttributesDto = await firstValueFrom(
                this.sdkMapFilterService.mapFilterControllerGetFilterAttributeValues({
                    attribute: this.layerAttributeForm().get('column')!.value,
                    projectId: this.projectBloc.value()!.id,
                    table: this.layerTableName()!,
                }),
            );

            this.hasTooManyDistinctValues.set(!!attributeValues.hasTooManyDistinctValues);

            if (attributeValues.hasTooManyDistinctValues || attributeValues.isEmpty) {
                this.layerAttributeValueOptions.set([]);

                return;
            }

            this.layerAttributeValueOptions.set(
                attributeValues.attributes!.map((value: string) => ({ label: value, value })),
            );
        } catch {
            this.layerAttributeValueOptions.set([]);
        } finally {
            this.isLoading.set(false);
        }
    }
}
